import def from "ajv/dist/vocabularies/discriminator"
import { FamilyOffersSectionProps } from "./FamilyOffersSectionProps.d"
import tw from "twin.macro"
import { parseDisclaimerBlocks } from "../../../../helpers"
import { toggleDisclaimersModal } from "../../../../contexts/Disclaimers/actions"
import { useContext, useEffect, useRef, useState } from "react"
import { DisclaimersContext } from "../../../../contexts/Disclaimers"
import { OffersClient } from "../../../../clients/OffersClient"
import { Offer } from "../../../../global"
import { LanguageContext } from "../../../../contexts/Language"
import AliceCarousel from "react-alice-carousel"
import Icon from "../../../atoms/Icon"
import { css } from "@emotion/react"
import useViewportSize from "../../../../hooks/useViewportSize"
import FamilyOfferCard from "./FamilyOfferCard"

const FamilyOffersSection: React.FC<FamilyOffersSectionProps> = ({
  title,
  family,
}) => {
  const [disclaimerState, dispatch] = useContext(DisclaimersContext)
  const { language } = useContext(LanguageContext)
  const [familyOffers, setFamilyOffers] = useState<Offer[]>([])
  const [currentItem, setCurrentItem] = useState(0)
  const [itemsPerSlide, setItemsPerSlide] = useState(3)
  const { width } = useViewportSize()
  const offerCarousel = useRef<AliceCarousel>(null)

  useEffect(() => {
    const seriesNames = family.routes.map(
      route => route.route.page.vehicle.series.name,
    )

    const getOffersData = async () => {
      try {
        const offersData = await OffersClient.getManyOffers({
          series: JSON.stringify(seriesNames),
        })
        if (offersData.offers) {
          setFamilyOffers(offersData.offers)
        }
      } catch (error) {
        console.error("Error fetching offers data", error)
      }
    }
    seriesNames && getOffersData()
  }, [family])

  useEffect(() => {
    const updateItemsPerSlide = () => {
      if (window.innerWidth >= 1280) {
        setItemsPerSlide(3)
      } else if (window.innerWidth >= 768) {
        setItemsPerSlide(2)
      } else {
        setItemsPerSlide(1)
      }
    }
    updateItemsPerSlide()
    window.addEventListener("resize", updateItemsPerSlide)
    return () => window.removeEventListener("resize", updateItemsPerSlide)
  }, [])

  const chunkedItems = []
  for (let i = 0; i < familyOffers.length; i += itemsPerSlide) {
    chunkedItems.push(familyOffers.slice(i, i + itemsPerSlide))
  }
  const items = chunkedItems.map((chunk, index) => (
    <div key={`family-offers-${index}`} css={[tw`flex`]}>
      {chunk.map(offer => {
        return (
          <div
            css={[tw`w-full mx-0 lg:(w-[420px]) relative`]}
            key={`family-offer-${offer._id}`}
          >
            <FamilyOfferCard
              offer={offer}
              cardType={offer?.cardLabel!}
              cardLabel={
                language === "es"
                  ? offer?.displayLabelES!
                  : offer?.displayLabel!
              }
            />
          </div>
        )
      })}
    </div>
  ))

  const renderPrevButton = ({ isDisabled }: { isDisabled?: boolean }) => {
    return (
      <button
        css={[
          tw`absolute left-1 top-[45%] -translate-y-1/2 z-50 flex justify-center items-center cursor-pointer w-12 h-8 bg-gunmetal rounded-full transition-all hover:bg-gray-400 focus-visible:(bg-gray-400)`,
          tw`md:(left-6)`,
          tw`lg:(left-16)`,
          isDisabled ? tw`hidden` : tw`flex`,
        ]}
      >
        <Icon.Chevron direction="left" color="white" css={[tw`h-4`]} />
      </button>
    )
  }

  const renderNextButton = ({ isDisabled }: { isDisabled?: boolean }) => {
    return (
      <button
        css={[
          tw`absolute right-1 top-[45%] -translate-y-1/2 z-50 flex justify-center items-center cursor-pointer w-12 h-8 bg-gunmetal rounded-full transition-all hover:(bg-gray-400) focus-visible:(bg-gray-400)`,
          tw`md:(right-6)`,
          tw`lg:(right-16)`,
          isDisabled ? tw`hidden` : tw`flex`,
        ]}
      >
        <Icon.Chevron direction="right" color="white" css={[tw`h-4`]} />
      </button>
    )
  }

  return (
    <section css={[tw`py-32 bg-wildSand`]}>
      {/* DESKTOP VIEW */}
      <div css={[tw`max-w-[1440px] mx-auto flex flex-col items-center gap-5`]}>
        <h2
          css={[
            tw`text-3xl block leading-[104.6%] text-center mb-6 lg:text-5xl`,
          ]}
        >
          {parseDisclaimerBlocks(title, selection =>
            dispatch(toggleDisclaimersModal(selection)),
          )}
        </h2>
        <div
          css={[tw`flex flex-nowrap h-full w-full text-center justify-center`]}
        >
          <AliceCarousel
            mouseTracking
            items={items}
            disableDotsControls
            paddingLeft={
              width <= 768 ? 0 : width > 768 && width < 1024 ? 65 : 90
            }
            paddingRight={
              width <= 768 ? 0 : width > 768 && width < 1024 ? 65 : 90
            }
            renderPrevButton={renderPrevButton}
            renderNextButton={renderNextButton}
            onSlideChanged={e => {
              setCurrentItem(e.item + e.itemsInSlide)
            }}
            ref={offerCarousel}
          />
          {/* Progress bar that fills by % */}
        </div>
        <div
          css={[
            tw`border-transparent h-[3px] bg-gray-400  mx-auto w-[200px] lg:w-[800px]`,
          ]}
        >
          <div
            css={[
              tw`h-[5px] mt-[-1px] border border-gunmetal bg-gunmetal`,
              css`
                width: ${(currentItem * (100 / items.length)).toString()}%;
              `,
            ]}
          />
        </div>
      </div>
    </section>
  )
}
export default FamilyOffersSection
